import React, { useLayoutEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { cn } from '@/utils';
import { IoCloseSharp } from 'react-icons/io5';

interface BannerItem {
  image?: string;
  type?: string;
}

interface WelcomeBannerProps {
  banners?: BannerItem[];
  className?: string;
  isLoading?: boolean;
  setShowWelcomeBanner: (show: boolean) => void;
}

export const WelcomeBanner: React.FC<WelcomeBannerProps> = ({
  banners,
  className,
  isLoading,
  setShowWelcomeBanner,
}) => {
  const [imagesLoaded, setImagesLoaded] = useState<Record<number, boolean>>({});
  const [topOffset, setTopOffset] = useState(0);
  // The popup takes the first banner's real shape so it can't outgrow the image.
  const [ratio, setRatio] = useState<string | null>(null);
  const captureRatio = (img: HTMLImageElement) => {
    if (img.naturalWidth && img.naturalHeight) {
      setRatio(`${img.naturalWidth} / ${img.naturalHeight}`);
    }
  };

  // Until the first image reports its real ratio, the popup uses a placeholder
  // shape (see the aspectRatio fallback below) so it appears instantly.
  // Start the banner just below the Deposit/Withdraw row so those buttons
  // stay visible above it.
  useLayoutEffect(() => {
    const measure = () => {
      const row = document.querySelector('.dwtx-row');
      setTopOffset(row ? row.getBoundingClientRect().bottom : 0);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const allLoaded =
    Array.isArray(banners) && banners.length > 0 && banners.every((_, idx) => imagesLoaded[idx]);

  // Show skeleton while data is still fetching OR while images haven't loaded yet
  const showSkeleton = isLoading || !allLoaded;
  const hasMultipleSlides = (banners?.length ?? 0) > 1;

  return (
    <div
      onClick={(e) => setShowWelcomeBanner(false)}
      className="fixed inset-0 z-[80] flex items-start justify-center bg-black/70 px-3"
      style={{ paddingTop: topOffset + 12, paddingBottom: 12 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'relative w-[85%] max-w-[400px] rounded-[0.625rem] animate-fade-in overflow-y-auto',
          showSkeleton && 'animate-pulse bg-slate-700/60',
          className
        )}
        style={{ aspectRatio: ratio ?? '4 / 5' }}
      >
        <button
          type="button"
          aria-label="Close welcome banner"
          className="absolute right-2 top-2 z-20 flex size-8 items-center justify-center rounded-full bg-black text-xl leading-none text-white"
          onClick={() => setShowWelcomeBanner(false)}
        >
          <IoCloseSharp />
        </button>

        {hasMultipleSlides && (
          <>
            <button className="banner-prev rotate-90 absolute left-3 top-1/2 z-10 -translate-y-1/2">
              <i className="icon-arrow-down text-[1.5rem] opacity-70" />
            </button>

            <button className="banner-next -rotate-90 absolute right-3 top-1/2 z-10 -translate-y-1/2 text-white">
              <i className="icon-arrow-down text-[1.5rem] opacity-70" />
            </button>
          </>
        )}

        {/* ── Slider ── always rendered so images can preload; hidden until ready */}
        {Array.isArray(banners) && banners.length === 1 && banners[0] && (
          <img
            src={banners[0].image}
            alt={banners[0].type}
            className={cn(
              'block w-full h-auto rounded-[0.625rem] transition-opacity duration-200',
              imagesLoaded[0] ? 'opacity-100' : 'opacity-0'
            )}
            fetchPriority="high"
            style={{ display: 'block', margin: 0, verticalAlign: 'top' }}
            onLoad={(e) => {
              captureRatio(e.currentTarget);
              setImagesLoaded((prev) => ({ ...prev, 0: true }));
            }}
            onError={() => setImagesLoaded((prev) => ({ ...prev, 0: true }))}
          />
        )}

        {Array.isArray(banners) && banners.length > 0 && hasMultipleSlides && (
          <Swiper
            modules={[Autoplay, Navigation]}
            navigation={
              hasMultipleSlides
                ? {
                    prevEl: '.banner-prev',
                    nextEl: '.banner-next',
                  }
                : false
            }
            autoplay={
              hasMultipleSlides
                ? {
                    delay: 5000,
                    disableOnInteraction: false,
                  }
                : false
            }
            loop={hasMultipleSlides}
            className="w-full !h-auto"
          >
            {banners.map((banner, idx) => (
              <SwiperSlide key={idx} className="!h-auto">
                <img
                  src={banner.image}
                  alt={banner.type}
                  className={cn(
                    'block w-full h-auto rounded-[0.625rem] transition-opacity duration-200',
                    imagesLoaded[idx] ? 'opacity-100' : 'opacity-0'
                  )}
                  fetchPriority="high"
                  onLoad={(e) => {
                    if (idx === 0) captureRatio(e.currentTarget);
                    setImagesLoaded((prev) => ({ ...prev, [idx]: true }));
                  }}
                  onError={() => setImagesLoaded((prev) => ({ ...prev, [idx]: true }))}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </div>
    </div>
  );
};
